import React, { useCallback, useMemo } from 'react';

import PageRedirect from 'pages/PageRedirect';
import { Outlet, Route, Routes } from 'react-router-dom';

import { Box } from '@mui/material';

import { PublicRoutes } from './PublicRoutes';
import AppSuspense from 'components/AppSuspense';
import UnderConstruction from 'components/Layout/components/UnderConstruction';
import LayoutSwitcher from 'components/Layout/LayoutSwitcher';
import RequireMenuInitialized from 'components/RequireMenuInitialized';
import Spin from 'components/Spin';
import { useUser } from 'global/UserContext';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import AdvisorsAccessHub from 'microfrontends/Advisors/AccessHub/Advisors';
import Courses from 'microfrontends/Education/AdminEducation/Courses';
import Lessons from 'microfrontends/Education/AdminEducation/Lessons';
import AdvisorsMarketplace from 'microfrontends/Marketplace/Advisors';
import GeneralMarketplace from 'microfrontends/Marketplace/General';
import ServicesMarketplace from 'microfrontends/Marketplace/Services';
import AccessHub from 'microfrontends/Meetings/AccessHub';
import AgendaConfiguration from 'pages/AgendaConfiguration';
import AutomationV2 from 'pages/AutomationV2';
import BasicPersonalData from 'pages/BasicPersonalData';
import BusinessBio from 'pages/BusinessBio';
import { Cockpit } from 'pages/Cockpit';
import CollectiveProfileMembers from 'pages/CollectiveProfileMembers';
import CompanyData from 'pages/CompanyData';
import GovernanceEvents from 'pages/DigitalGovernance/GovernanceEvent';
import GovernanceThematicGroups from 'pages/DigitalGovernance/GovernanceThematicGroups';
import GovernanceUsers from 'pages/DigitalGovernance/GovernanceUsers';
import { EducationFavorites } from 'pages/EducationFavorites';
import { EducationMyCourses } from 'pages/EducationMyCourses';
import { EducationShowcase } from 'pages/EducationShowcase';
import Error from 'pages/Errors/Error';
import IndividualBio from 'pages/IndividualBio';
import InvitesPage from 'pages/Invites';
import EndUserResponse from 'pages/LeadRequest/EndUserResponse';
import LeadRequestOrchestration from 'pages/LeadRequest/LeadRequestOrchestration';
import SponsorRequest from 'pages/LeadRequest/SponsorRequest';
import SolutionsMarketplace from 'pages/Marketplace';
import { AtivacaoPresencial } from 'pages/NetworkingExperience/AtivacaoPresencial';
import { OrchestratorEducationCategories } from 'pages/OrchestratorEducationCategories';
import RouteGuard from 'routes/RouteGuard';

interface RouteComponent {
  documentId: string;
  component: React.ReactNode;
  path?: string;
  isPublic?: boolean;
}

const AuthRoutes = () => {
  // Hooks necessários para mapeamento de rotas
  const { user } = useUser();
  const menuRoutes = useMenuRoutes();

  // 2. TODOS OS useMemo DEVEM VIR AQUI
  const publicRoutes = useMemo<RouteComponent[]>(() => PublicRoutes, []);

  const protectedRoutes = useMemo<RouteComponent[]>(() => {
    // Função para mapear componentes por documentId
    const getComponentMap = (documentId: string) => {
      const componentMap: Record<string, React.ReactNode> = {
        'marketplace-advisor': <SolutionsMarketplace type="advisor" title="Assessores" />,
        'marketplace-courses': (
          <SolutionsMarketplace type="courses" title="Cursos" categoryId={1} />
        ),
        'marketplace-services': <SolutionsMarketplace type="services" title="Serviços" />,
        'marketplace-plans': <SolutionsMarketplace type="plans" title="Planos" categoryId={5} />,
        bhnrs7gefue5v5ntzqg3ls74: <Cockpit isAgenda={false} />,
        eh04q5jlhm52tnh1tthycrkd: <Cockpit isAgenda={true} />,
        c7jp5768k53sra3u6bv5ynxg: <IndividualBio />,
        orggc8xfsjhi06uojgmcnifc: <CollectiveProfileMembers />,
        md1wm0c8mnn6t8j0k62aywmy: <EducationShowcase />,
        blm0udkzzi2tixjhb6knp3h4: <EducationMyCourses />,
        bf2t3hejquw6g50ok47yujs8: <EducationFavorites />,
        x8vn7552j2z8motvcpzq2dp5: <AtivacaoPresencial key="ativacao-presencial-component" />,
        // xxo09iaggq9nwdz1k6384su7: <PBIDash reportName="rmkr_general_networks" />,
        // ij94qlo086g27564ecpwuodc: <PBIDash reportName="rmkr_bio_networks" />,
        // qk2dzqrdfefnut2qtisy2zbl: <PBIDash reportName="rmkr_extended_community" />,
        // ld9rsfvvtjxk1awhj65vrmit: <PBIDash reportName="rmkr_individual_network" />,
        // wkfn4hkgdw13y86o9yxu41pq: <PBIDash reportName="rmkr_general_invitee_profile" />,
        xxo09iaggq9nwdz1k6384su7: <UnderConstruction />,
        ij94qlo086g27564ecpwuodc: <UnderConstruction />,
        qk2dzqrdfefnut2qtisy2zbl: <UnderConstruction />,
        ld9rsfvvtjxk1awhj65vrmit: <UnderConstruction />,
        wkfn4hkgdw13y86o9yxu41pq: <UnderConstruction />,
        ah06bmqiaqi7oxigam2836u1: <BasicPersonalData />,
        yuheaauamsvg289gn5ac2ogk: <CompanyData />,
        nwdjh4f3wj95xej5a30iri2q: <InvitesPage />,
        wfrhqdqgq51gqj4kcswgvg0r: <GovernanceThematicGroups />,
        b0i2qi0dhqmelqonbhn3pzn5: <GovernanceUsers />,
        bv5n1nkso2vewyx5gilyblgu: <GovernanceEvents />,
        qec5xuvx1dq05uuczz52irrq: <OrchestratorEducationCategories />,
        nhaa2ypwl4zbsfu8xkaf0k17: <Courses key="courses" />,
        opp497btmueyywpgzbr7e835: <Lessons key="lessons" userId={user?.id || ''} />,
        tegujpnuqitwwjxdw3mqhf00: <UnderConstruction />,
        meeting_room: <></>,
        rp6pr14xcoibtn5p9qd0ip8z: <AdvisorsMarketplace />,
        nql7so3er7p473mkgzawkla7: <GeneralMarketplace categoryId={1} />,
        hqqnrfpu1ox2iqdzoa81pmt3: <GeneralMarketplace categoryId={5} />,
        f0pzvvpx9kvxz2ejkubibdys: <GeneralMarketplace categoryId={11} />,
        g58dvhpca9eirq6gln0f2fyi: <ServicesMarketplace />,
        s56a1gugf03ngwgsp4zr3nho: <AgendaConfiguration />,
        vpquicugxm4w5svpjji0b3ox: <AccessHub />,
        ttuofh4gmf7xs8vv8ee6xi6s: <AdvisorsAccessHub />,
        lqzbm5ir5gnxh1zsdtufcd7w: <AutomationV2 />,
        ki9ao5w75ds5ykbeytbvm2dd: <BusinessBio />,
        k2acxd0qf2eiwtevt4xn5oa1: <LeadRequestOrchestration />,
        lk0yriz57vt600loevyonvq2: <SponsorRequest />,
        jxm4lel4ot7ihnap5xwkmtyf: <EndUserResponse />,
        pivrzyizslnoydu96jbgf674: <PageRedirect documentId="pivrzyizslnoydu96jbgf674" />,
      };
      return componentMap[documentId] || null; // Retorna null em vez de Error para permitir renderização dinâmica
    };

    // Mapeia todas as rotas do menuRoutes dinamicamente
    return menuRoutes.map(route => ({
      documentId: route.documentId,
      component: getComponentMap(route.documentId),
      path: route.path,
    }));
  }, [menuRoutes, user?.id]);

  // 3. TODOS OS useCallback DEVEM VIR AQUI
  const getComponentByDocumentId = useCallback(
    (documentId: string, path?: string) => {
      // Se temos um path específico, prioriza a correspondência exata
      if (path) {
        const route = protectedRoutes.find(r => r.path === path);
        if (route) {
          // Se o componente for null, significa que deve ser renderizado dinamicamente
          if (route.component === null) {
            return null; // Permite que o LayoutSwitcher renderize dinamicamente
          }
          return route.component || <Error />;
        }
      }

      // Fallback: busca por documentId
      const route = protectedRoutes.find(r => r.documentId === documentId);
      if (route?.component === null) {
        return null; // Permite que o LayoutSwitcher renderize dinamicamente
      }
      return route?.component || <Error />;
    },
    [protectedRoutes]
  );

  const getPublicRoutes = useCallback(() => publicRoutes, [publicRoutes]);

  // Renderização da estrutura de rotas
  // RouteGuard gerencia toda a lógica de acesso (auth, validação, redirects)
  // AuthRoutes apenas define a estrutura das rotas
  return (
    <AppSuspense>
      <RouteGuard>
        <Routes>
          {/* Rotas públicas - não requerem autenticação */}
          {getPublicRoutes().map(route => (
            <Route key={route.documentId} path={route.path} element={route.component} />
          ))}

          {/* Rotas protegidas - requerem autenticação (gerenciada pelo RouteGuard) */}
          <Route
            path="/"
            element={
              <LayoutSwitcher>
                <RequireMenuInitialized>
                  <Outlet />
                </RequireMenuInitialized>
              </LayoutSwitcher>
            }
          >
            {protectedRoutes.map(route => {
              const component = getComponentByDocumentId(route.documentId, route.path);

              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    component === null ? (
                      // Componente dinâmico - renderizado pelo LayoutSwitcher
                      <LayoutSwitcher>
                        <RequireMenuInitialized>
                          <Outlet />
                        </RequireMenuInitialized>
                      </LayoutSwitcher>
                    ) : (
                      // Componente estático
                      component
                    )
                  }
                />
              );
            })}

            {/* Fallback para rotas não encontradas - RouteGuard gerencia o 404 */}
            <Route
              path="*"
              element={
                <Box>
                  <Spin />
                </Box>
              }
            />
          </Route>
        </Routes>
      </RouteGuard>
    </AppSuspense>
  );
};

export default AuthRoutes;
